import React from "react";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Pagination from "@mui/material/Pagination";
import PropertyCard from "../../components/PropertyCard";
import propertyData from "../../components/PropertyCard/defaultData";
import ProductSidebar from "../../components/ProductSidebar";
import Dropdown from "../../components/FormElements/Dropdown";
import unavailableImage from "../../assets/property_unavailable.png";
import { Property } from "../../types/Property";
import { PropertySearchFilters } from "../../swr/properties";
import { PAGE_SIZE_OPTIONS } from "../../utils/propertySearchParams";
import PropertyFilters from "./PropertyFilters";
import styles from "./propertyGrid.module.scss";

export interface PropertyGridProps {
  properties: Property[];
  loading: boolean;
  error: boolean;
  totalRecords: number;
  totalPages: number;
  page: number;
  pageSize: number;
  filters: PropertySearchFilters;
  // The URL has search params, so an empty result can offer to clear them.
  isFiltered: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onFiltersChange: (filters: PropertySearchFilters) => void;
}

const pageSizeOptions = PAGE_SIZE_OPTIONS.map((size) => ({
  value: size,
  label: `Per Page: ${size}`,
}));

const PropertyGrid: React.FC<PropertyGridProps> = ({
  properties,
  loading,
  error,
  totalRecords,
  totalPages,
  page,
  pageSize,
  filters,
  isFiltered,
  onPageChange,
  onPageSizeChange,
  onFiltersChange,
}) => {
  const first = (page - 1) * pageSize + 1;
  const last = first + properties.length - 1;

  return (
    <div className="ltn_product-area ltn_product-gutter mb-120">
      <div className="container">
        <div className="row">
          <div className="col-lg-8">
            <div className="ltn__shop-options">
              <ul className="justify-content-between">
                <li className={styles.resultCount}>
                  {!loading && totalRecords > 0 && (
                    <span>
                      Showing {first}–{last} of {totalRecords} properties
                    </span>
                  )}
                </li>
                <li>
                  <Dropdown
                    variant={2}
                    useObjectValues
                    optionsObj={pageSizeOptions}
                    value={pageSize}
                    valueCollector={(value) => {
                      const size = Number(value);
                      if (size && size !== pageSize) onPageSizeChange(size);
                    }}
                  />
                </li>
              </ul>
            </div>

            <div className="ltn__product-tab-content-inner ltn__product-grid-view">
              <div className="row">
                {loading &&
                  [0, 1, 2, 3].map((i) => (
                    <div className="col-xl-6 col-sm-6 col-12" key={i}>
                      <Skeleton variant="rectangular" width="100%">
                        <PropertyCard property={propertyData} />
                      </Skeleton>
                    </div>
                  ))}

                {!loading && error && (
                  <div className="col-xl-12">
                    <p>We couldn't load properties. Please try again.</p>
                  </div>
                )}

                {!loading && !error && properties.length === 0 && (
                  <div className="col-xl-12">
                    <div className={styles.unavailable}>
                      <img src={unavailableImage} alt="No properties found" />
                    </div>
                    {isFiltered && (
                      <div className={styles.unavailable}>
                        <button
                          type="button"
                          className="btn theme-btn-1 btn-effect-1"
                          onClick={() => onFiltersChange({})}
                        >
                          Clear all filters
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {!loading &&
                  properties.map((property) => (
                    <div className="col-xl-6 col-sm-6 col-12" key={property.id}>
                      <PropertyCard property={property} />
                    </div>
                  ))}
              </div>
            </div>

            {totalPages > 1 && (
              <Stack spacing={2} sx={{ marginBottom: "30px", alignItems: "center" }}>
                <Pagination
                  count={totalPages}
                  page={Math.min(page, totalPages)}
                  onChange={(_, newPage) => onPageChange(newPage)}
                  size="large"
                  variant="outlined"
                  color="standard"
                />
              </Stack>
            )}
          </div>

          <div className="col-lg-4">
            <ProductSidebar>
              <h3 className="mb-10 animated fadeIn">Advance Information</h3>
              <PropertyFilters filters={filters} onChange={onFiltersChange} />
            </ProductSidebar>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyGrid;
